import type { Blackout, BlackoutCreate, BlackoutUpdate } from '@lms/shared-types'
import { apiRequest } from './client'

export function listBlackouts(): Promise<Blackout[]> {
  return apiRequest<Blackout[]>('/api/v1/blackouts')
}

export function createBlackout(input: BlackoutCreate): Promise<Blackout> {
  return apiRequest<Blackout>('/api/v1/blackouts', {
    method: 'POST',
    body: input,
  })
}

export function updateBlackout(id: string, input: BlackoutUpdate): Promise<Blackout> {
  return apiRequest<Blackout>(`/api/v1/blackouts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  })
}

export function deleteBlackout(id: string): Promise<null> {
  return apiRequest<null>(`/api/v1/blackouts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export interface OverlappingLeaveItem {
  leaveId: string
  employeeId: string
  employeeName: string
  startDate: string
  endDate: string
  type: string
  daysCount: string
  status: string
  reason: string
  resettable: boolean
}

export function listOverlappingLeaves(blackoutId: string): Promise<OverlappingLeaveItem[]> {
  return apiRequest<OverlappingLeaveItem[]>(
    `/api/v1/blackouts/${encodeURIComponent(blackoutId)}/overlapping-leaves`,
  )
}

export interface AffectedLeavePreviewItem {
  leaveId: string
  employeeId: string
  employeeName: string
  startDate: string
  endDate: string
  type: string
  daysCount: string
  reason: string
}

export function previewAffectedLeaves(input: {
  startDate: string
  endDate: string
  teamIds: string[]
}): Promise<AffectedLeavePreviewItem[]> {
  return apiRequest<AffectedLeavePreviewItem[]>('/api/v1/blackouts/preview-affected', {
    method: 'POST',
    body: input,
  })
}

export function resetLeaveToPending(
  leaveId: string,
  triggeringBlackoutId: string,
  triggeringBlackoutName: string,
): Promise<{ leaveId: string; status: string }> {
  return apiRequest<{ leaveId: string; status: string }>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/reset-to-pending`,
    {
      method: 'POST',
      body: { triggeringBlackoutId, triggeringBlackoutName },
    },
  )
}

export interface EscalationTargets {
  head: string | null
  director: string | null
  execDirector: string | null
}

export function getEscalationTargets(): Promise<EscalationTargets> {
  return apiRequest<EscalationTargets>('/api/v1/blackouts/escalation-targets')
}

export interface BlackoutNoticeDraft {
  draft: string
}

/** Employee confirms they want to keep a leave the blackout reconciler
 *  flipped to PENDING. Clears `pending_reason` so it routes to the
 *  manager queue, and enqueues the manager Slack DM. */
export function keepLeaveAfterBlackout(
  leaveId: string,
): Promise<{ leaveId: string; status: string }> {
  return apiRequest<{ leaveId: string; status: string }>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/keep-after-blackout`,
    { method: 'POST' },
  )
}
